import { EnglishProficiencyAssessmentQuery } from 'generated/graphql';

export type TEnglishProficiencyAssessment =
  EnglishProficiencyAssessmentQuery['englishProficiencyAssessment'];

export type TEnglishProficiencyQuestion =
  TEnglishProficiencyAssessment['questions'][number];

/** The two question kinds, narrowed off the fields only one of them carries.
 * `kind` cannot do this — codegen types it as the whole enum on both. */
export type TEnglishProficiencyMultipleChoiceQuestion = Extract<
  TEnglishProficiencyQuestion,
  { answers: unknown }
>;
export type TEnglishProficiencyShortAnswerQuestion = Extract<
  TEnglishProficiencyQuestion,
  { blankCount: unknown }
>;

export const isMultipleChoiceQuestion = (
  question: TEnglishProficiencyQuestion
): question is TEnglishProficiencyMultipleChoiceQuestion =>
  'answers' in question;

export const isShortAnswerQuestion = (
  question: TEnglishProficiencyQuestion
): question is TEnglishProficiencyShortAnswerQuestion =>
  'blankCount' in question;

/** One candidate's answer to one item, in the shape the save mutation takes.
 * Exactly one of the two is ever set — the server refuses both and neither. */
export type TEnglishProficiencyAnswer = {
  selectedAnswerIndex: number | null;
  blankAnswers: string[] | null;
};

export type TEnglishProficiencyAnswers = Record<
  number,
  TEnglishProficiencyAnswer | undefined
>;

/** The literal gap marker in a short-answer question's text. */
export const ENGLISH_PROFICIENCY_BLANK = '<blank>';

/**
 * Splits a question's text on its gap markers: the returned strings are the
 * literal text either side of each blank, so `n` blanks yield `n + 1` pieces
 * (some empty, when the question opens or closes on a gap).
 */
export const splitOnBlanks = (text: string): string[] =>
  text.split(ENGLISH_PROFICIENCY_BLANK);

/**
 * Seeds the local answers from what the server already holds, so a candidate
 * coming back to a sitting sees what they answered before.
 */
export const seedAnswers = (
  responses: TEnglishProficiencyAssessment['responses']
): TEnglishProficiencyAnswers =>
  responses.reduce<TEnglishProficiencyAnswers>((answers, response) => {
    answers[response.questionIndex] = {
      selectedAnswerIndex: response.selectedAnswerIndex ?? null,
      blankAnswers: response.blankAnswers ?? null,
    };
    return answers;
  }, {});

/**
 * Whether an item counts as answered.
 *
 * A short answer only counts once every one of its gaps is filled. That is
 * deliberate and not only cosmetic: grading is all-or-nothing, and the server
 * ends the sitting the moment the sixtieth answer lands — so persisting a
 * half-typed word would close the test mid-thought.
 */
export const isQuestionAnswered = (
  question: TEnglishProficiencyQuestion,
  answer: TEnglishProficiencyAnswer | undefined
): boolean => {
  if (!answer) {
    return false;
  }
  if (isShortAnswerQuestion(question)) {
    const blankAnswers = answer.blankAnswers ?? [];
    return (
      blankAnswers.length === question.blankCount &&
      blankAnswers.every(blankAnswer => blankAnswer.trim().length > 0)
    );
  }
  return answer.selectedAnswerIndex != null;
};

export const countAnsweredQuestions = (
  questions: TEnglishProficiencyQuestion[],
  answers: TEnglishProficiencyAnswers
): number =>
  questions.filter(question =>
    isQuestionAnswered(question, answers[question.index])
  ).length;

/** `mm:ss`, and `h:mm:ss` once an accommodation pushes the clock past an hour. */
export const formatRemainingTime = (totalSeconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  const paddedSeconds = String(seconds).padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${paddedSeconds}`;
  }
  return `${minutes}:${paddedSeconds}`;
};
